import React, { useRef, useEffect } from 'react';

export const NumericAnswerField = ({
  id,
  disabled = false,
  hasNumValidationError = false,
  hasUOMValidationError = false,
  uomRequired = false,
  fieldValue = null,
  unitsOfMeasure = [],
  defaultUOM = '',
  relevantUoms = [],
  historicalDataSupported = false,
  historicalDataYears = [],
  historicalResponse,
  currentPeriod,
  onAnswerChange,
  onUOMChange,
  onHistoricalAnswerChange,
  onHistoricalUOMChange
}) => {
  const inputRef = useRef(null);
  const historicalInputRefs = useRef({});

  // Block wheel events on the main input
  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      const handleWheel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      input.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        input.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Block wheel events on historical inputs
  useEffect(() => {
    const inputs = Object.values(historicalInputRefs.current);
    const cleanupFunctions = [];

    inputs.forEach(input => {
      if (input) {
        const handleWheel = (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };

        input.addEventListener('wheel', handleWheel, { passive: false });
        cleanupFunctions.push(() => {
          input.removeEventListener('wheel', handleWheel);
        });
      }
    });

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [historicalDataYears]);

  return (
    <>
      <div className='w-100 FormView_box_flex align-items-center'>
        <div className='w-100'>
          <input type="number"
            ref={inputRef}
            disabled={disabled}
            className={`form-control ${hasNumValidationError ? 'border-danger' : ''}`}
            value={fieldValue}
            name={id}
            onKeyDown={(e) => {
              // Prevent arrow keys from scrolling
              if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onTouchStart={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
            onChange={(e) => onAnswerChange(e)}
          />
        </div>
        {
          uomRequired
            ?
            <div className='ms-2 w-100  d-flex align-items-center'>
              <select className={`form-select ${hasUOMValidationError ? 'border-danger' : ''}`}
                disabled={disabled}
                value={defaultUOM}
                onChange={(e) => onUOMChange(e)}>
                <option key='' value=''>Select</option>
                {
                  relevantUoms?.length > 0
                    ?
                    relevantUoms.sort((a, b) => a.localeCompare(b))?.map(u => {
                      return (<option key={u} value={u}>
                        {unitsOfMeasure?.length > 0 ? unitsOfMeasure?.find(unit => unit?.value === u)?.label : u}
                      </option>
                      )
                    })
                    :
                    unitsOfMeasure?.map(u => {
                      return <option key={u.value} value={u.value}>{u.label}</option>
                    })
                }
              </select>
              {
                historicalDataSupported &&
                <span className='mx-2'>{currentPeriod}</span>
              }
            </div>
            :
            <>
              {
                historicalDataSupported &&
                <div className='ms-2 w-30'>{currentPeriod}</div>
              }
            </>
        }
      </div>
      {
        historicalDataSupported &&
        historicalDataYears?.map((year, yIndex) => {
          return (
            <div className='w-100 my-2 FormView_box_flex align-items-center' key={`histField-${id}-${yIndex}`}>
              <div className='w-100'>
                <input type="number"
                  ref={(el) => {
                    if (el) {
                      historicalInputRefs.current[`${id}-${yIndex}`] = el;
                    }
                  }}
                  disabled={disabled}
                  className="form-control"
                  value={historicalResponse ? historicalResponse[year]?.responses?.find(r => r.formField?.id === id)?.numericResponse ?? '' : ''}
                  name={`inputField-${id}-${yIndex}`}
                  onKeyDown={(e) => {
                    // Prevent arrow keys from scrolling
                    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onTouchStart={(e) => e.preventDefault()}
                  onTouchMove={(e) => e.preventDefault()}
                  onChange={(e) => onHistoricalAnswerChange({ value: e.target.value, year: year })}
                />
              </div>
              {
                uomRequired
                  ?
                  <div className='ms-2 w-100 d-flex align-items-center'>
                    <select className='form-select'
                      disabled={disabled}
                      value={historicalResponse ? historicalResponse[year]?.responses?.find(r => r.formField?.id === id)?.uom ?? '' : ''}
                      onChange={(e) => onHistoricalUOMChange({ value: e.target.value, year: year })}>
                      <option key='' value=''>Select</option>
                      {
                        relevantUoms?.length > 0
                          ?
                          relevantUoms.sort((a, b) => a.localeCompare(b))?.map(u => {
                            return (<option key={u} value={u}>
                              {unitsOfMeasure?.length > 0 ? unitsOfMeasure?.find(unit => unit?.value === u)?.label : u}
                            </option>
                            )
                          })
                          :
                          unitsOfMeasure?.map(u => {
                            return <option key={u.value} value={u.value}>{u.label}</option>
                          })
                      }
                    </select>
                    <span className='mx-2'>{year}</span>
                  </div>
                  :
                  <div className='ms-2 w-30'>{year}</div>
              }
            </div>
          );
        })
      }
    </>
  );
};

export default NumericAnswerField;
